import {
  getDefaultPersona,
  isFTEOrContractorEmail,
  IRemoAdminPersonaJSON,
  RemoAdminTool,
  DataModeCheckTypes,
  checkDataScope,
  RemoAdminPersonaDataScope,
} from '@scale/shared/types/remoAdmin';
import { IUserJson } from '@scale/shared/types/user';
import React, { useEffect, useMemo, useState } from 'react';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import {
  ReactHOC,
  withFR,
  withHocDisplayName,
  withNonReactStatics,
} from 'react-scale/lib/hocHelpers';

// This object is a bit of a hack,
// allowing us to read the selected
// project and auditbatch from within withV2Timer.tsx,
// which is unable to read the right RemoAdminCtx.
export const RemoAdminTimerData = {
  projectId: '',
  auditBatchId: '',
};

export interface IRemoAdminContext {
  // If true, the component is being rendered inside RemoAdmin. If false, component is in Corp or somewhere else
  isRemoAdmin: boolean;
  canRequestProject: boolean;
  userId: string;
  remoAdminPersonas: IRemoAdminPersonaJSON[];
  setSelectedProject: React.Dispatch<React.SetStateAction<string>>;
  selectedProject: string;
  selectedProjectName: string;
  setSelectedProjectName: React.Dispatch<React.SetStateAction<string>>;
  currentPersona?: IRemoAdminPersonaJSON;
  selfServeTools?: boolean;
  setCurrentPersona: React.Dispatch<React.SetStateAction<IRemoAdminPersonaJSON | undefined>>;
  isTSM: boolean;
  isTSMDRI: boolean;
  isQMOperator: boolean;
  isQIR: boolean;
  isQAPlus: boolean;
  isQA: boolean;
  isFraudAuditor: boolean;
  hidePII: boolean;
  hideCustomer: boolean;
  canMoveTeamWorkers: boolean;

  // used to track audit ids in remoadmin timers
  auditBatchId: string;
  setAuditBatchId: React.Dispatch<React.SetStateAction<string>>;
}

const defaultRemoAdminContext: IRemoAdminContext = {
  isRemoAdmin: false,
  canRequestProject: false,
  isQAPlus: false,
  isQA: false,
  isFraudAuditor: false,
  userId: '',
  remoAdminPersonas: [],
  selectedProject: '',
  selectedProjectName: '',
  setSelectedProject: (_id: React.SetStateAction<string>) => undefined,
  setSelectedProjectName: (_name: React.SetStateAction<string>) => undefined,
  setCurrentPersona: (_persona: React.SetStateAction<IRemoAdminPersonaJSON | undefined>) =>
    undefined,
  hidePII: false,
  hideCustomer: false,
  isQMOperator: false,
  isTSM: false,
  isTSMDRI: false,
  isQIR: false,
  canMoveTeamWorkers: false,
  auditBatchId: '',
  setAuditBatchId: (_id: React.SetStateAction<string>) => undefined,
};

const RemoAdminContext = React.createContext<IRemoAdminContext>(defaultRemoAdminContext);
export const useRemoAdminCtx = () => React.useContext(RemoAdminContext);

export const RemoAdminCtxProvider: React.FC<{
  user: IUserJson;
  url?: any;
  children?: React.ReactNode;
}> = ({ user, url, children }) => {
  const initialProject = url?.query?.projectId || url?.query?.project;
  const [selectedProject, setSelectedProject] = useState<string>(initialProject ?? '');
  const [selectedProjectName, setSelectedProjectName] = useState<string>(initialProject ?? '');
  const [auditBatchId, setAuditBatchId] = useState<string>('');
  const [currentPersona, setCurrentPersona] = useState(
    getDefaultPersona(user.remoAdminPersonas ?? []),
  );
  const selfServeToolFlag = useUnleashFeatureFlag(
    'selfserve-remoadmin-tools',
    { userId: user._id },
    undefined,
  );

  const {
    isQMOperator,
    hideCustomer,
    isTSM,
    isTSMDRI,
    isQIR,
    canMoveTeamWorkers,
    hidePII,
    isQAPlus,
    isQA,
    isFraudAuditor,
  } = useMemo(() => {
    const checkScope = (scopes: DataModeCheckTypes[]) =>
      !!currentPersona && checkDataScope(currentPersona, scopes);
    const qmDScope = checkScope([RemoAdminPersonaDataScope.QM]);
    const tsmDSCope = checkScope([
      RemoAdminPersonaDataScope.TSM,
      RemoAdminPersonaDataScope.TSMEngagementDRI,
    ]);
    const tsmDRIScope = checkScope([RemoAdminPersonaDataScope.TSMEngagementDRI]);
    const qirDSCope = checkScope([RemoAdminPersonaDataScope.QIR]);
    const qaPlusScope = checkScope([RemoAdminPersonaDataScope.QAPlus]);
    const qaScope = checkScope([RemoAdminPersonaDataScope.QA]);
    const fraudAuditorScope = checkScope([RemoAdminPersonaDataScope.FraudAuditor]);
    const hasTeamsTool = !!currentPersona?.tools?.includes(RemoAdminTool.Teams);
    const shouldHidePII = !qmDScope && !qirDSCope && !tsmDSCope;
    const shouldHideCustomer = !qirDSCope && !qmDScope;
    return {
      isQMOperator: qmDScope,
      isTSM: tsmDSCope,
      isTSMDRI: tsmDRIScope,
      isQIR: qirDSCope,
      canMoveTeamWorkers: hasTeamsTool && (tsmDSCope || qmDScope),
      hidePII: shouldHidePII,
      isQAPlus: qaPlusScope,
      isQA: qaScope,
      hideCustomer: shouldHideCustomer,
      isFraudAuditor: fraudAuditorScope,
    };
  }, [currentPersona]);

  const { remoAdminPersonas, userIsContractorOrFTE } = useMemo(() => {
    return {
      remoAdminPersonas: user.remoAdminPersonas ?? [],
      userIsContractorOrFTE: isFTEOrContractorEmail(user.email),
    };
  }, [user]);

  const selfServeTools = useMemo<boolean>(() => {
    return !!(user.isCorporateUser || currentPersona?.isSelfServe || selfServeToolFlag);
  }, [user, currentPersona, selfServeToolFlag]);

  const canRequestProject = useMemo<boolean>(() => {
    const individualProjects = !currentPersona?.applyToAllProjects;
    const allowedDataScope = isQMOperator || isTSM || isQIR;
    return userIsContractorOrFTE && allowedDataScope && individualProjects;
  }, [currentPersona, userIsContractorOrFTE, isQMOperator, isTSM, isQIR]);

  useEffect(() => {
    RemoAdminTimerData.projectId = selectedProject;
  }, [selectedProject]);

  useEffect(() => {
    RemoAdminTimerData.auditBatchId = auditBatchId;
  }, [auditBatchId]);

  return (
    <RemoAdminContext.Provider
      value={{
        userId: user._id,
        isRemoAdmin: user.isRemoAdmin ?? false,
        canRequestProject,
        selfServeTools,
        remoAdminPersonas,
        setSelectedProject,
        selectedProjectName,
        setSelectedProjectName,
        selectedProject,
        setCurrentPersona,
        currentPersona,
        hideCustomer,
        hidePII,
        isQMOperator,
        isTSM,
        isTSMDRI,
        isQIR,
        canMoveTeamWorkers,
        isQAPlus,
        isQA,
        isFraudAuditor,
        auditBatchId,
        setAuditBatchId,
      }}
    >
      {children}
    </RemoAdminContext.Provider>
  );
};

export const withRemoAdminCtx: ReactHOC<{ ref?: any } & any, any> = WrappedComponent =>
  withFR(
    withNonReactStatics(WrappedComponent)(
      withHocDisplayName('withRemoAdminCtx')(props => {
        const remoAdminCtx = useRemoAdminCtx();
        return <WrappedComponent ref={props.forwardedRef} remoAdminCtx={remoAdminCtx} {...props} />;
      }),
    ),
  );

export const extendPropsWithTimers = (props: any, pageName: string) => {
  return {
    ...props,
    usePageTracker: true,
    pageType: `remo-admin:${pageName}`,
  };
};

export default RemoAdminContext;
